import styles from 'css/src/components/common/video-downloads/DownloadOptionsGrid.module.scss';

import React from 'react';
import Immutable from 'immutable';

import Spinner from 'js/src/components/common/ui/Spinner';
import UpsellIcon, { ICON_SIZES } from 'js/src/components/common/ui/UpsellIcon';
import Tooltip, { Placement } from 'js/src/components/common/ui/Tooltip';

export type DownloadOption = {
    id: string;
    /** Material Symbols glyph name. */
    icon: string;
    text: string;
    /** The file extension, shown under the label. */
    hint: string;
    /** Shown on hover; explains what the artifact is for. */
    description: string;
    inProgress: boolean;
    /** Blocks the action for a reason other than an in-flight download. */
    disabled?: boolean;
    /** Marks the option as a paid upgrade: shows a crown, and still fires onClick so the
     *  caller can open the upsell. */
    locked?: boolean;
    onClick: () => void;
};

type DownloadOptionsGridProps = {
    options: DownloadOption[];
    /** Omit to render the tiles on their own. */
    heading?: string;
    /**
     * Render tooltips inside the grid rather than on the body. Needed only when the grid sits in
     * a high z-index layer (the publish popover), where a body-level tooltip would be painted
     * behind it. In normal page flow this constrains the tooltip to the grid's box, which pushes
     * it downward and grows the page, so leave it off.
     */
    containTooltips?: boolean;
};

/**
 * The download options as a grid of stacked tiles: icon, label, then file type. Shared by the
 * publish panel and the share page so both offer the same controls.
 */
const FALLBACK_PLACEMENTS = Immutable.List([Placement.BOTTOM]);

const DownloadOptionsGrid = React.memo(function DownloadOptionsGrid({
    options,
    heading,
    containTooltips = false,
}: DownloadOptionsGridProps) {
    const containerRef = React.useRef<HTMLDivElement>(null);

    return (
        <div className="d-flex flex-column gap-2" ref={containerRef}>
            {heading && (
                <h6 className={`${styles['download-options-heading']} m-l5-0`}>{heading}</h6>
            )}
            <div className={styles['download-options-grid']}>
                {options.map((option) => (
                    <Tooltip
                        key={option.id}
                        title={option.description}
                        trigger="hover"
                        placement={Placement.TOP}
                        fallbackPlacements={FALLBACK_PLACEMENTS}
                        container={containTooltips ? containerRef : undefined}
                    >
                        <button
                            type="button"
                            className={`${styles['download-option']} d-flex flex-column align-items-center gap-1 p-l5-12`}
                            disabled={option.inProgress || option.disabled}
                            onClick={option.onClick}
                        >
                            <span
                                className={`${styles['download-option-media']} d-flex align-items-center justify-content-center`}
                            >
                                {option.inProgress ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <i
                                        className={`material-symbols-outlined ${styles['download-option-icon']}`}
                                    >
                                        {option.icon}
                                    </i>
                                )}
                            </span>
                            <span className={`${styles['download-option-text']} text-center`}>
                                {option.locked && (
                                    <UpsellIcon
                                        isOutlined
                                        className="workspace-premium me-l5-2"
                                        size={ICON_SIZES.XS}
                                    />
                                )}
                                {option.text}
                            </span>
                            <span className={styles['download-option-hint']}>{option.hint}</span>
                        </button>
                    </Tooltip>
                ))}
            </div>
        </div>
    );
});

export default DownloadOptionsGrid;
